import {useState, useEffect, useMemo, useCallback, useTransition, memo} from 'react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@common-web/components/ui/table';
import {
  Plus,
  X,
  ArrowRight,
  ArrowDown,
  Zap,
  Settings,
  Database,
  FileText,
  Info,
  Wand2,
  Eye,
  Check,
} from 'lucide-react';
import {
  PayrollFileColumnTransformation,
  PayrollFileValueTransformation,
  PayrollFileRow,
  PayrollFileColumnTransformationColumn,
} from '@api/lib/payrollProcessing/types';
import {
  dedupePayrollColumns,
  getEmptyMapping,
  getBaseColumnsFromMapping,
} from '../utils/transformationUtils';
import {Mapping, ExtractedFile, ExtractedColumn} from '../types';
import {ColumnSelect} from './ColumnSelect';
import {
  COLUMN_TRANSFORMATION_OPTIONS,
  VALUE_TRANSFORMATION_OPTIONS,
} from '@client/pages/payroll/constants/transformationOptions';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@common-web/components/ui/sheet';
import {ScrollArea} from '@common-web/components/ui/scroll-area';
import {toast} from '@common-web/hooks/use-toast';
import {Switch} from '@common-web/components/ui/switch';
import {toTitleCase} from '@common/lib/utils';
import {getTransformationPreview} from '@client/pages/payroll/utils/previewUtils';
import {validateTransformationColumns} from '@client/pages/payroll/utils/validationUtils';

export const TransformationEditor = memo(
  ({
    extracted,
    mapping,
    onChange,
  }: {
    extracted: ExtractedFile[];
    mapping: Mapping | undefined;
    onChange: (next: Mapping) => void;
  }) => {
    const [isAddingColumn, setIsAddingColumn] = useState(false);
    const [isAddingValueTransformation, setIsAddingValueTransformation] = useState(false);
    const [isValueSelectOpen, setIsValueSelectOpen] = useState(false);
    const [addingFallbackFor, setAddingFallbackFor] = useState<number | null>(null);
    const [showFallbackColumns, setShowFallbackColumns] = useState(false);
    const [local, setLocal] = useState<Mapping>(() => mapping || getEmptyMapping());

    const {sampleRows, fileRowsMap} = useMemo(() => {
      const sampleRows: PayrollFileRow[] = extracted.length > 0 ? [{}] : [];
      const fileRowsMap: Record<string, PayrollFileRow[]> = {};

      // Build file rows map for preview
      extracted.forEach((file) => {
        const sampleRow: PayrollFileRow = {};
        file.columns.forEach((col) => {
          sampleRow[col.name] = col.samples[0] || '';
        });
        if (file.pivotColumns) {
          file.pivotColumns.forEach((col) => {
            sampleRow[col.name] = col.samples[0] || '';
          });
        }
        fileRowsMap[file.file.fileId] = [sampleRow];
      });

      return {sampleRows, fileRowsMap};
    }, [extracted]);

    const combinedColumns = useMemo(() => {
      return extracted.flatMap((f) => {
        const regularCols = f.columns.map((c) => ({...c, fileId: f.file.fileId}));
        const pivotCols = (f.pivotColumns || []).map((c) => ({
          name: c.name,
          index: c.index,
          samples: c.samples,
          fileId: f.file.fileId,
          pivotKeyValue: c.pivotKeyValue,
        }));
        return [...regularCols, ...pivotCols];
      });
    }, [extracted]);

    const columnsByFileId = useMemo(() => {
      const map: Record<string, ExtractedColumn[]> = {};
      extracted.forEach((file) => {
        const allColumns = [...file.columns];
        if (file.pivotColumns) {
          allColumns.push(
            ...file.pivotColumns.map((pc) => ({
              name: pc.name,
              index: pc.index,
              samples: pc.samples,
              pivotKeyValue: pc.pivotKeyValue,
            })),
          );
        }
        map[file.file.fileId] = allColumns;
      });
      return map;
    }, [extracted]);

    useEffect(() => {
      if (mapping) {
        setLocal(mapping);
        const columns = getBaseColumnsFromMapping(mapping);
        const hasFallbacks = columns.some((column) => (column.fallbackColumns?.length ?? 0) > 0);
        if (hasFallbacks) {
          setShowFallbackColumns(true);
        }
      } else {
        setLocal(getEmptyMapping());
        setShowFallbackColumns(false);
      }

      setAddingFallbackFor(null);
    }, [mapping]);

    const currentColumnTransformationType = local?.columnTransformation?.type || 'NONE';

    useEffect(() => {
      if (extracted.length === 0) {
        setIsAddingColumn(false);
      }
    }, [extracted]);

    const computePreview = useCallback(
      (next: Mapping): Mapping => {
        try {
          const preview = getTransformationPreview(
            next.columnTransformation,
            next.valueTransformations || [],
            fileRowsMap,
          );
          next.exampleValuePreTransformation = preview.preTransformation;
          next.exampleValuePostTransformation = preview.postTransformation;
        } catch (error) {
          console.warn('Error calculating transformation preview:', error);
          next.exampleValuePreTransformation = '';
          next.exampleValuePostTransformation = '';
        }
        return next;
      },
      [sampleRows, fileRowsMap],
    );

    const updateLocal = useCallback(
      (partial: Partial<Mapping>): Mapping => {
        const next = computePreview({...local, ...partial});
        setLocal(next);
        onChange(next);
        return next;
      },
      [local, computePreview, onChange],
    );

    const setColumnTransformationType = useCallback(
      (type: PayrollFileColumnTransformation['type']) => {
        setShowFallbackColumns(false);
        if (type === 'NONE') {
          updateLocal({
            columnTransformation: {type: 'NONE', column: {name: '', index: 0, fileId: ''}},
          });
          setAddingFallbackFor(null);
        } else if (type === 'COLUMN_FORMULA') {
          updateLocal({columnTransformation: {type, columns: [], formula: ''}});
        } else if (type === 'PIVOT_COLUMN') {
          // Pivot columns are configured separately, not through this editor
          updateLocal({
            columnTransformation: {
              type: 'PIVOT_COLUMN',
              pivotColumn: {fileId: '', pivotKeyValue: '', name: '', index: 0},
            },
          });
        } else if (type === 'STATIC_VALUE') {
          updateLocal({columnTransformation: {type: 'STATIC_VALUE', staticValue: ''}});
        } else {
          updateLocal({columnTransformation: {type, columns: []}});
        }
      },
      [updateLocal],
    );

    const addColumn = useCallback(
      (fileId: string, index: number) => {
        const file = extracted.find((f) => f.file.fileId === fileId);
        // Search in both regular columns and pivot columns
        let col = file?.columns.find((c) => c.index === index);
        let pivotKeyValue: string | undefined;
        if (!col && file?.pivotColumns) {
          const pivotCol = file.pivotColumns.find((c) => c.index === index);
          if (pivotCol) {
            col = {name: pivotCol.name, index: pivotCol.index, samples: pivotCol.samples};
            pivotKeyValue = pivotCol.pivotKeyValue;
          }
        }
        if (!col) {
          return;
        }

        if (currentColumnTransformationType === 'NONE') {
          updateLocal({
            columnTransformation: {
              type: 'NONE',
              column: {name: col.name, index: col.index, fileId, pivotKeyValue},
            },
            exampleValuePreTransformation: col.samples?.[0] || '',
            exampleValuePostTransformation: col.samples?.[0] || '',
          });
        } else {
          const existing =
            (
              local?.columnTransformation as {
                columns?: PayrollFileColumnTransformationColumn[];
              }
            )?.columns || [];
          updateLocal({
            columnTransformation: {
              ...local.columnTransformation,
              columns: dedupePayrollColumns([
                ...existing,
                {name: col.name, index: col.index, fileId, pivotKeyValue},
              ]),
            } as PayrollFileColumnTransformation,
          });
        }
        setAddingFallbackFor(null);
      },
      [extracted, currentColumnTransformationType, local, updateLocal],
    );

    const removeColumnAt = useCallback(
      (idx: number) => {
        const cols =
          (local?.columnTransformation as {columns?: PayrollFileColumnTransformationColumn[]})
            ?.columns || [];
        const next = [...cols];
        next.splice(idx, 1);
        updateLocal({
          columnTransformation: {
            ...local.columnTransformation,
            columns: next,
          } as PayrollFileColumnTransformation,
        });
        if (addingFallbackFor === idx) {
          setAddingFallbackFor(null);
        }
      },
      [local, updateLocal, addingFallbackFor],
    );

    const addFallbackColumn = useCallback(
      (columnIdx: number, fileId: string, index: number) => {
        const cols =
          (local?.columnTransformation as {columns?: PayrollFileColumnTransformationColumn[]})
            ?.columns || [];
        const target = cols[columnIdx];
        if (!target) return;
        const availableColumns = columnsByFileId[fileId] || [];
        const sourceColumn = availableColumns.find((c) => c.index === index);
        if (!sourceColumn) return;
        const existingFallbacks = target.fallbackColumns || [];
        const alreadyExists = existingFallbacks.some(
          (fallback) => fallback.fileId === fileId && fallback.index === index,
        );
        if (alreadyExists) {
          setAddingFallbackFor(null);
          return;
        }
        const updatedColumn: PayrollFileColumnTransformationColumn = {
          ...target,
          fallbackColumns: [
            ...existingFallbacks,
            {name: sourceColumn.name, index: sourceColumn.index, fileId},
          ],
        };
        const nextColumns = [...cols];
        nextColumns[columnIdx] = updatedColumn;
        updateLocal({
          columnTransformation: {
            ...local.columnTransformation,
            columns: nextColumns,
          } as PayrollFileColumnTransformation,
        });
        setAddingFallbackFor(null);
      },
      [columnsByFileId, local, updateLocal],
    );

    const removeFallbackColumn = useCallback(
      (columnIdx: number, fallbackIdx: number) => {
        const cols =
          (local?.columnTransformation as {columns?: PayrollFileColumnTransformationColumn[]})
            ?.columns || [];
        const target = cols[columnIdx];
        if (!target || !target.fallbackColumns) return;
        const nextFallbacks = [...target.fallbackColumns];
        nextFallbacks.splice(fallbackIdx, 1);
        const updatedColumn: PayrollFileColumnTransformationColumn = {
          ...target,
          fallbackColumns: nextFallbacks.length > 0 ? nextFallbacks : undefined,
        };
        const nextColumns = [...cols];
        nextColumns[columnIdx] = updatedColumn;
        updateLocal({
          columnTransformation: {
            ...local.columnTransformation,
            columns: nextColumns,
          } as PayrollFileColumnTransformation,
        });
      },
      [local, updateLocal],
    );

    const addValueTransform = useCallback(
      (type: string) => {
        const next = [...(local.valueTransformations || [])];
        if (type === 'NONE') next.push({type});
        if (
          type === 'TRIM' ||
          type === 'TO_UPPERCASE' ||
          type === 'TO_LOWERCASE' ||
          type === 'REMOVE_NON_NUMERIC' ||
          type === 'REMOVE_NON_ALPHABETIC' ||
          type === 'PARSE_NUMBER' ||
          type === 'CONVERT_BLANK_TO_EMPTY'
        ) {
          next.push({type});
        }
        if (type === 'VALUE_MAPPING') {
          next.push({type, mappings: [{from: '', to: ''}]});
        }
        if (type === 'DEFAULT_IF_EMPTY') {
          next.push({type, defaultValue: ''});
        }
        if (type === 'DEFAULT_UNLESS_VALUE') {
          next.push({type, unlessValue: '', defaultValue: ''});
        }
        if (type === 'PAD_STRING') {
          next.push({type, padPosition: 'START', padCharacter: '0', targetLength: 9});
        }
        if (type === 'SUBSTRING') {
          next.push({type, startIndex: 1, length: undefined});
        }
        if (type === 'SPLIT_AND_EXTRACT') {
          next.push({type, delimiter: ', ', partIndex: 1});
        }
        updateLocal({valueTransformations: next});
      },
      [local, updateLocal],
    );

    const removeValueTransformAt = useCallback(
      (idx: number) => {
        const next = [...(local.valueTransformations || [])];
        next.splice(idx, 1);
        updateLocal({valueTransformations: next});
      },
      [local, updateLocal],
    );

    const updateValueMap = useCallback(
      (idx: number, mapIdx: number, key: 'from' | 'to', value: string) => {
        const next = [...(local.valueTransformations || [])];
        const m = {...next[idx]} as {mappings?: {from: string; to: string}[]};
        const arr = [...(m.mappings || [])];
        arr[mapIdx] = {...arr[mapIdx], [key]: value};
        m.mappings = arr;
        next[idx] = m as PayrollFileValueTransformation;
        updateLocal({valueTransformations: next});
      },
      [local, updateLocal],
    );

    const addValueMapRow = useCallback(
      (idx: number) => {
        const next = [...(local.valueTransformations || [])];
        const m = {...next[idx]} as {mappings?: {from: string; to: string}[]};
        m.mappings = [...(m.mappings || []), {from: '', to: ''}];
        next[idx] = m as PayrollFileValueTransformation;
        updateLocal({valueTransformations: next});
      },
      [local, updateLocal],
    );

    const removeValueMapRow = useCallback(
      (idx: number, mapIdx: number) => {
        const next = [...(local.valueTransformations || [])];
        const m = {...next[idx]} as {mappings?: {from: string; to: string}[]};
        const arr = [...(m.mappings || [])];
        arr.splice(mapIdx, 1);
        m.mappings = arr;
        next[idx] = m as PayrollFileValueTransformation;
        updateLocal({valueTransformations: next});
      },
      [local, updateLocal],
    );

    const sumDuplicateColumnOptionAllowed =
      extracted.length > 1 &&
      (currentColumnTransformationType === 'COLUMN_ADDITION' ||
        currentColumnTransformationType === 'COLUMN_FORMULA');

    const fallbackColumnSelectionAllowed =
      currentColumnTransformationType === 'COLUMN_ADDITION' ||
      currentColumnTransformationType === 'COLUMN_FORMULA';

    return (
      <div className='space-y-8'>
        <div className='text-center'>
          <div className='flex items-center justify-center gap-4 mb-4'>
            <div className='flex items-center gap-2 px-3 py-2 bg-internal-brand-blue/10 rounded-lg'>
              <Database className='h-4 w-4 text-internal-brand-blue' />
              <span className='text-sm font-medium text-internal-brand-blue'>Source Columns</span>
            </div>
            <ArrowRight className='h-5 w-5 text-internal-brand-gray' />
            <div className='flex items-center gap-2 px-3 py-2 bg-amber/10 rounded-lg'>
              <Settings className='h-4 w-4 text-amber' />
              <span className='text-sm font-medium text-amber'>Transform</span>
            </div>
            <ArrowRight className='h-5 w-5 text-internal-brand-gray' />
            <div className='flex items-center gap-2 px-3 py-2 bg-emerald/10 rounded-lg'>
              <FileText className='h-4 w-4 text-emerald' />
              <span className='text-sm font-medium text-emerald'>Target Field</span>
            </div>
          </div>
          <p className='text-sm text-internal-brand-gray'>
            Use the options below to configure how data from your source files should be transformed
            and mapped to the target field.
          </p>
        </div>
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='bg-gradient-to-r from-internal-brand-blue/5 to-internal-brand-blue/10 border-b'>
            <CardTitle className='text-lg font-semibold text-internal-brand-navy flex items-center gap-2'>
              <Database className='h-5 w-5 text-internal-brand-blue' />
              Step 1: Column Transformations
            </CardTitle>
          </CardHeader>
          <CardContent className='p-6'>
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
              <div className='space-y-3'>
                <Label className='text-sm font-medium text-internal-brand-navy'>
                  How to combine columns
                </Label>
                <Select
                  value={currentColumnTransformationType}
                  onValueChange={setColumnTransformationType}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLUMN_TRANSFORMATION_OPTIONS.map((transformationOption) => (
                      <SelectItem key={transformationOption.type} value={transformationOption.type}>
                        {transformationOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className='p-3 bg-gray-50 rounded-lg border border-gray-200'>
                  <div className='flex items-start gap-2'>
                    <Info className='h-4 w-4 text-internal-brand-blue mt-0.5 shrink-0' />
                    {(() => {
                      const columnTransformationInfo = COLUMN_TRANSFORMATION_OPTIONS.find(
                        (option) => option.type === currentColumnTransformationType,
                      );
                      if (columnTransformationInfo) {
                        return (
                          <div className='text-sm text-internal-brand-gray-dark'>
                            <div className='font-medium mb-1'>{columnTransformationInfo.label}</div>
                            <div>{columnTransformationInfo.description}</div>
                            {columnTransformationInfo.example && (
                              <div className='mt-1 italic'>
                                Example: {columnTransformationInfo.example}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              </div>
              <div className='space-y-3 lg:col-span-2'>
                <Label className='text-sm font-medium text-internal-brand-navy'>
                  {currentColumnTransformationType === 'STATIC_VALUE'
                    ? 'Enter static value'
                    : 'Select source columns'}
                </Label>
                {currentColumnTransformationType === 'STATIC_VALUE' ? (
                  <Input
                    value={
                      (
                        local.columnTransformation as Extract<
                          PayrollFileColumnTransformation,
                          {type: 'STATIC_VALUE'}
                        >
                      )?.staticValue ?? ''
                    }
                    placeholder='Enter a fixed value for every row...'
                    maxLength={500}
                    onChange={(e) => {
                      updateLocal({
                        columnTransformation: {
                          type: 'STATIC_VALUE',
                          staticValue: e.target.value,
                        },
                      });
                    }}
                    className='h-10 border-gray-200 focus:border-internal-brand-blue focus:ring-internal-brand-blue'
                  />
                ) : currentColumnTransformationType === 'NONE' ? (
                  <ColumnSelect
                    columns={combinedColumns}
                    value={(() => {
                      const ct = local.columnTransformation;
                      if (
                        ct?.type === 'NONE' &&
                        ct.column &&
                        ct.column.fileId &&
                        ct.column.fileId.length > 0
                      ) {
                        return `${ct.column.fileId}:${ct.column.index}`;
                      }
                      return undefined;
                    })()}
                    onValueChange={(value) => {
                      const [fileId, idx] = value.includes(':')
                        ? value.split(':')
                        : [extracted[0]?.file.fileId || '', value];
                      addColumn(fileId, Number(idx));
                    }}
                    placeholder='Select column…'
                    className='h-10 border-gray-200 focus:border-internal-brand-blue focus:ring-internal-brand-blue'
                  />
                ) : (
                  <div className='space-y-2'>
                    {!isAddingColumn ? (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setIsAddingColumn(true)}
                        className='border-internal-brand-blue text-internal-brand-blue hover:bg-internal-brand-blue hover:text-white'
                      >
                        <Plus className='h-4 w-4 mr-1' /> Add column
                      </Button>
                    ) : (
                      <div className='flex items-end gap-2'>
                        <div className={'flex-1'}>
                          <Label className='text-xs text-internal-brand-gray mb-1 block'>
                            Column
                          </Label>
                          <ColumnSelect
                            columns={combinedColumns}
                            value={undefined}
                            onValueChange={(value) => {
                              const [fileId, idx] = value.includes(':')
                                ? value.split(':')
                                : [extracted[0]?.file.fileId || '', value];
                              addColumn(fileId, Number(idx));
                              setIsAddingColumn(false);
                            }}
                            placeholder='Select column…'
                            className='h-10 w-full border-gray-200 focus:border-internal-brand-blue focus:ring-internal-brand-blue'
                          />
                        </div>
                        <div className='flex gap-2'>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => setIsAddingColumn(false)}
                            className='text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700'
                          >
                            <X className='h-4 w-4' />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {currentColumnTransformationType !== 'NONE' && (
                  <div className={'mt-2 rounded-sm overflow-hidden border border-gray-300'}>
                    <Table>
                      <TableHeader className={'bg-gray-100'}>
                        <TableRow>
                          <TableHead>
                            {currentColumnTransformationType === 'COLUMN_FALLBACK'
                              ? 'Priority'
                              : currentColumnTransformationType === 'COLUMN_FORMULA'
                                ? 'Variable'
                                : ''}
                          </TableHead>
                          <TableHead>File</TableHead>
                          <TableHead>Column</TableHead>
                          {fallbackColumnSelectionAllowed && showFallbackColumns && (
                            <TableHead>Fallback column</TableHead>
                          )}
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(
                          (
                            local?.columnTransformation as {
                              columns?: PayrollFileColumnTransformationColumn[];
                            }
                          )?.columns || []
                        ).map((transformation, idx) => (
                          <TableRow
                            key={`${transformation.fileId}-${transformation.index}-${idx}`}
                            className={'even:bg-gray-50 even:border-y even:border-gray-200'}
                          >
                            <TableCell>
                              {currentColumnTransformationType === 'COLUMN_FALLBACK'
                                ? `${idx + 1}`
                                : currentColumnTransformationType === 'COLUMN_FORMULA'
                                  ? `$${idx + 1}`
                                  : ''}
                            </TableCell>
                            <TableCell>{transformation.fileId}</TableCell>
                            <TableCell>{transformation.name}</TableCell>
                            {fallbackColumnSelectionAllowed && showFallbackColumns && (
                              <TableCell>
                                {transformation.fallbackColumns &&
                                transformation.fallbackColumns.length > 0 ? (
                                  <div className='space-y-2'>
                                    {transformation.fallbackColumns.map((fallback, fallbackIdx) => (
                                      <div
                                        key={`${fallback.fileId}-${fallback.index}-${fallbackIdx}`}
                                        className='flex items-center justify-between gap-2 rounded-sm border border-gray-200 bg-white px-2 py-1 text-xs text-internal-brand-navy'
                                      >
                                        <span className='font-mono text-gray-700'>
                                          {fallback.fileId} → {fallback.name}
                                        </span>
                                        <Button
                                          variant='ghost'
                                          size='icon'
                                          className='h-6 w-6 text-red-600 hover:bg-red-50 hover:text-red-700'
                                          onClick={() => removeFallbackColumn(idx, fallbackIdx)}
                                        >
                                          <X className='h-3 w-3' />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <Button
                                    variant='outline'
                                    size='sm'
                                    className='border-dashed border-gray-300 text-gray-600 hover:bg-gray-50'
                                    onClick={() => setAddingFallbackFor(idx)}
                                  >
                                    <Plus className='mr-1 h-3 w-3' /> Add fallback
                                  </Button>
                                )}
                                {addingFallbackFor === idx && (
                                  <div className='mt-2 flex items-end gap-2'>
                                    <ColumnSelect
                                      columns={(columnsByFileId[transformation.fileId] || []).map(
                                        (col) => ({
                                          ...col,
                                          fileId: transformation.fileId,
                                        }),
                                      )}
                                      value={undefined}
                                      onValueChange={(value) => {
                                        const [fid, idxStr] = value.includes(':')
                                          ? value.split(':')
                                          : [transformation.fileId, value];
                                        addFallbackColumn(idx, fid, Number(idxStr));
                                      }}
                                      placeholder='Select column'
                                      className='h-10 flex-1 border-gray-200 focus:border-internal-brand-blue focus:ring-internal-brand-blue'
                                    />
                                    <Button
                                      variant='ghost'
                                      size='sm'
                                      onClick={() => setAddingFallbackFor(null)}
                                      className='text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700'
                                    >
                                      <X className='h-4 w-4' />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            )}
                            <TableCell className='text-right'>
                              <Button
                                variant='ghost'
                                className={
                                  'text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700'
                                }
                                size='sm'
                                onClick={() => removeColumnAt(idx)}
                              >
                                <X className='h-4 w-4' />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
            {currentColumnTransformationType === 'COLUMN_FORMULA' && (
              <div className='mt-4 space-y-2'>
                <Label className='text-sm font-medium text-internal-brand-navy'>
                  Enter your formula below
                </Label>
                <div className='text-sm text-gray-600'>
                  Use $1, $2, ... to reference the columns above in order. Supports +, -, *, / and
                  parentheses.
                </div>
                <Input
                  value={
                    (
                      local.columnTransformation as Extract<
                        PayrollFileColumnTransformation,
                        {type: 'COLUMN_FORMULA'}
                      >
                    )?.formula !== undefined
                      ? (
                          local.columnTransformation as Extract<
                            PayrollFileColumnTransformation,
                            {type: 'COLUMN_FORMULA'}
                          >
                        ).formula
                      : ''
                  }
                  onChange={(e) => {
                    updateLocal({
                      columnTransformation: {
                        ...local.columnTransformation,
                        formula: e.target.value,
                      } as PayrollFileColumnTransformation,
                    });
                  }}
                />
              </div>
            )}
            {sumDuplicateColumnOptionAllowed && (
              <div className='mt-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3'>
                <Switch
                  checked={Boolean(
                    (
                      local.columnTransformation as
                        | Extract<PayrollFileColumnTransformation, {type: 'COLUMN_ADDITION'}>
                        | Extract<PayrollFileColumnTransformation, {type: 'COLUMN_FORMULA'}>
                    )?.aggregateDuplicateRows,
                  )}
                  onCheckedChange={(checked) => {
                    updateLocal({
                      columnTransformation: {
                        ...local.columnTransformation,
                        aggregateDuplicateRows: checked,
                      } as
                        | Extract<PayrollFileColumnTransformation, {type: 'COLUMN_ADDITION'}>
                        | Extract<PayrollFileColumnTransformation, {type: 'COLUMN_FORMULA'}>,
                    });
                  }}
                />
                <div className='text-sm text-internal-brand-navy'>
                  <div className='font-medium'>Sum duplicate payroll file rows</div>
                  <p className='text-xs text-gray-600'>
                    When enabled, rows that share the same file link will add these source columns
                    together before merging with other files. Use this when your payroll file has
                    multiple rows per employee that should be combined into one total.
                  </p>
                </div>
              </div>
            )}
            {fallbackColumnSelectionAllowed && (
              <div className='mt-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3'>
                <Switch
                  checked={showFallbackColumns}
                  onCheckedChange={(checked) => {
                    setShowFallbackColumns(checked);
                    if (!checked) {
                      const cols =
                        (
                          local?.columnTransformation as {
                            columns?: PayrollFileColumnTransformationColumn[];
                          }
                        )?.columns || [];
                      const nextColumns = cols.map((c) => ({
                        ...c,
                        fallbackColumns: undefined,
                      }));
                      updateLocal({
                        columnTransformation: {
                          ...local.columnTransformation,
                          columns: nextColumns,
                        } as PayrollFileColumnTransformation,
                      });
                    }
                  }}
                />
                <div className='text-sm text-internal-brand-navy'>
                  <div className='font-medium'>Enable fallback columns</div>
                  <p className='text-xs text-gray-600'>
                    Allow specifying alternative columns to use if the primary column is empty.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='bg-gradient-to-r from-amber/5 to-amber/10 border-b'>
            <CardTitle className='text-lg font-semibold text-internal-brand-navy flex items-center gap-2'>
              <Zap className='h-5 w-5 text-amber' />
              Step 2: Value Transformations
            </CardTitle>
          </CardHeader>
          <CardContent className='p-6'>
            <div className='space-y-6'>
              <div className='flex items-center gap-2'>
                {!isAddingValueTransformation ? (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      setIsAddingValueTransformation(true);
                      setIsValueSelectOpen(true);
                    }}
                    className='border-amber text-amber hover:bg-amber hover:text-white'
                  >
                    <Plus className='h-4 w-4 mr-1' /> Add new transformation
                  </Button>
                ) : (
                  <Select
                    open={isValueSelectOpen}
                    onOpenChange={setIsValueSelectOpen}
                    onValueChange={(value) => {
                      addValueTransform(value);
                      setIsAddingValueTransformation(false);
                      setIsValueSelectOpen(false);
                    }}
                  >
                    <SelectTrigger className='w-72'>
                      <SelectValue placeholder='Choose a transformation…' />
                    </SelectTrigger>
                    <SelectContent>
                      {VALUE_TRANSFORMATION_OPTIONS.map((transformationOption) => {
                        return (
                          <SelectItem
                            key={transformationOption.type}
                            value={transformationOption.type}
                          >
                            <div className='flex flex-col'>
                              <span className='font-medium'>{transformationOption.label}</span>
                              <span className='text-xs text-gray-600'>
                                {transformationOption.description}
                              </span>
                              {!!transformationOption.example && (
                                <>
                                  <span className='text-xs text-gray-600'>Example: </span>
                                  <span className='font-mono text-xs text-gray-600'>
                                    {transformationOption.example || 'N/A'}
                                  </span>
                                </>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {(local.valueTransformations || []).length === 0 ? (
                <div className='text-center py-8 text-internal-brand-gray'>
                  <Zap className='h-12 w-12 mx-auto mb-3 text-internal-brand-gray-light' />
                  <div className='font-medium mb-1'>No transformations configured</div>
                  <div className='text-sm'>Add transformations above to modify your data</div>
                </div>
              ) : (
                <div className='space-y-4'>
                  <div className='flex items-center gap-2 mb-4'>
                    <div className='text-sm font-medium text-internal-brand-navy'>
                      Transformation Pipeline
                    </div>
                    <div className='flex-1 h-px bg-gray-200'></div>
                    <div className='text-xs text-internal-brand-gray'>
                      {(local.valueTransformations || []).length} step
                      {(local.valueTransformations || []).length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className='space-y-3'>
                    {(local.valueTransformations || []).map((valueTransformation, idx) => (
                      <div key={idx} className='flex items-start gap-3'>
                        <div className='flex flex-col items-center'>
                          <div className='w-8 h-8 bg-amber/10 rounded-full flex items-center justify-center text-amber font-semibold text-sm'>
                            {idx + 1}
                          </div>
                          {idx < (local.valueTransformations || []).length - 1 && (
                            <ArrowDown className='h-4 w-4 text-amber mt-2' />
                          )}
                        </div>
                        <Card className='flex-1 border-amber/20'>
                          <CardContent className='px-4 py-3'>
                            <div className='flex items-center justify-between'>
                              <div className='flex items-center gap-2'>
                                <Zap className='h-4 w-4 text-amber' />
                                <div className='text-sm text-amber font-medium'>
                                  {VALUE_TRANSFORMATION_OPTIONS.find(
                                    (option) => option.type === valueTransformation.type,
                                  )?.label ||
                                    toTitleCase(valueTransformation.type.replace(/_/g, ' '))}
                                </div>
                              </div>
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={() => removeValueTransformAt(idx)}
                                className='text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700'
                              >
                                <X className='h-4 w-4' />
                              </Button>
                            </div>
                            {(valueTransformation.type === 'VALUE_MAPPING' ||
                              valueTransformation.type === 'DEFAULT_IF_EMPTY' ||
                              valueTransformation.type === 'DEFAULT_UNLESS_VALUE' ||
                              valueTransformation.type === 'PAD_STRING' ||
                              valueTransformation.type === 'SUBSTRING' ||
                              valueTransformation.type === 'SPLIT_AND_EXTRACT') && (
                              <div className='mt-2 space-y-2'>
                                {valueTransformation.type === 'VALUE_MAPPING' ? (
                                  <>
                                    <Button
                                      variant='outline'
                                      size='sm'
                                      onClick={() => addValueMapRow(idx)}
                                    >
                                      <Plus className='h-4 w-4 mr-1' /> Add Mapping
                                    </Button>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>From</TableHead>
                                          <TableHead>To</TableHead>
                                          <TableHead></TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {(valueTransformation.mappings || []).map((m, mi) => (
                                          <TableRow key={mi}>
                                            <TableCell>
                                              <Input
                                                value={m.from}
                                                onChange={(e) =>
                                                  updateValueMap(idx, mi, 'from', e.target.value)
                                                }
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Input
                                                value={m.to}
                                                onChange={(e) =>
                                                  updateValueMap(idx, mi, 'to', e.target.value)
                                                }
                                              />
                                            </TableCell>
                                            <TableCell className='text-right'>
                                              <Button
                                                variant='ghost'
                                                size='sm'
                                                className={
                                                  'text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700'
                                                }
                                                onClick={() => removeValueMapRow(idx, mi)}
                                              >
                                                <X className='h-4 w-4' />
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </>
                                ) : valueTransformation.type === 'DEFAULT_IF_EMPTY' ? (
                                  <div className='flex items-center gap-2'>
                                    <Input
                                      value={valueTransformation.defaultValue || ''}
                                      placeholder='Enter default value'
                                      onChange={(e) => {
                                        const next = [...(local.valueTransformations || [])];

                                        next[idx] = {
                                          ...next[idx],
                                          defaultValue: e.target.value,
                                        } as PayrollFileValueTransformation;

                                        updateLocal({valueTransformations: next});
                                      }}
                                    />
                                  </div>
                                ) : valueTransformation.type === 'DEFAULT_UNLESS_VALUE' ? (
                                  <div className='flex flex-wrap items-end gap-3'>
                                    <div className='space-y-1'>
                                      <Label className='text-xs text-gray-600'>
                                        Unless value equals
                                      </Label>
                                      <Input
                                        value={valueTransformation.unlessValue || ''}
                                        placeholder='e.g., 0'
                                        className='w-32'
                                        onChange={(e) => {
                                          const next = [...(local.valueTransformations || [])];
                                          next[idx] = {
                                            ...next[idx],
                                            unlessValue: e.target.value,
                                          } as PayrollFileValueTransformation;
                                          updateLocal({valueTransformations: next});
                                        }}
                                      />
                                    </div>
                                    <div className='space-y-1'>
                                      <Label className='text-xs text-gray-600'>Change to</Label>
                                      <Input
                                        value={valueTransformation.defaultValue || ''}
                                        placeholder='e.g., Y'
                                        className='w-32'
                                        onChange={(e) => {
                                          const next = [...(local.valueTransformations || [])];
                                          next[idx] = {
                                            ...next[idx],
                                            defaultValue: e.target.value,
                                          } as PayrollFileValueTransformation;
                                          updateLocal({valueTransformations: next});
                                        }}
                                      />
                                    </div>
                                  </div>
                                ) : valueTransformation.type === 'PAD_STRING' ? (
                                  <div className='flex flex-wrap items-end gap-3'>
                                    <div className='space-y-1'>
                                      <Label className='text-xs text-gray-600'>Position</Label>
                                      <Select
                                        value={valueTransformation.padPosition || 'START'}
                                        onValueChange={(value: 'START' | 'END') => {
                                          const next = [...(local.valueTransformations || [])];
                                          next[idx] = {
                                            ...next[idx],
                                            padPosition: value,
                                          } as PayrollFileValueTransformation;
                                          updateLocal({valueTransformations: next});
                                        }}
                                      >
                                        <SelectTrigger className='w-28'>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value='START'>Start</SelectItem>
                                          <SelectItem value='END'>End</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className='space-y-1'>
                                      <Label className='text-xs text-gray-600'>Pad Character</Label>
                                      <Input
                                        value={valueTransformation.padCharacter || ''}
                                        placeholder='0'
                                        maxLength={1}
                                        className='w-20 text-center'
                                        onChange={(e) => {
                                          const next = [...(local.valueTransformations || [])];
                                          next[idx] = {
                                            ...next[idx],
                                            padCharacter: e.target.value.slice(0, 1),
                                          } as PayrollFileValueTransformation;
                                          updateLocal({valueTransformations: next});
                                        }}
                                      />
                                    </div>
                                    <div className='space-y-1'>
                                      <Label className='text-xs text-gray-600'>Target Length</Label>
                                      <Input
                                        type='number'
                                        value={valueTransformation.targetLength ?? ''}
                                        placeholder='9'
                                        min={1}
                                        max={100}
                                        className='w-24'
                                        onChange={(e) => {
                                          const next = [...(local.valueTransformations || [])];
                                          const parsed = parseInt(e.target.value, 10);
                                          next[idx] = {
                                            ...next[idx],
                                            targetLength: isNaN(parsed)
                                              ? 1
                                              : Math.min(100, Math.max(1, parsed)),
                                          } as PayrollFileValueTransformation;
                                          updateLocal({valueTransformations: next});
                                        }}
                                      />
                                    </div>
                                  </div>
                                ) : valueTransformation.type === 'SUBSTRING' ? (
                                  <div className='flex flex-wrap items-end gap-3'>
                                    <div className='space-y-1'>
                                      <Label className='text-xs text-gray-600'>
                                        Start Position
                                      </Label>
                                      <Input
                                        type='number'
                                        value={valueTransformation.startIndex ?? ''}
                                        placeholder='1'
                                        className='w-24'
                                        onChange={(e) => {
                                          const next = [...(local.valueTransformations || [])];
                                          const parsed = parseInt(e.target.value, 10);
                                          next[idx] = {
                                            ...next[idx],
                                            startIndex: isNaN(parsed) ? 1 : parsed,
                                          } as PayrollFileValueTransformation;
                                          updateLocal({valueTransformations: next});
                                        }}
                                      />
                                      <div className='text-[10px] text-gray-500'>
                                        1 = first, -1 = last
                                      </div>
                                    </div>
                                    <div className='space-y-1'>
                                      <Label className='text-xs text-gray-600'>
                                        Length{' '}
                                        <span className='text-gray-400 font-normal'>
                                          (optional)
                                        </span>
                                      </Label>
                                      <Input
                                        type='number'
                                        value={valueTransformation.length ?? ''}
                                        placeholder='All'
                                        min={1}
                                        max={1000}
                                        className='w-24'
                                        onChange={(e) => {
                                          const next = [...(local.valueTransformations || [])];
                                          const parsed = parseInt(e.target.value, 10);
                                          next[idx] = {
                                            ...next[idx],
                                            length:
                                              e.target.value === ''
                                                ? undefined
                                                : isNaN(parsed)
                                                  ? undefined
                                                  : Math.min(1000, Math.max(1, parsed)),
                                          } as PayrollFileValueTransformation;
                                          updateLocal({valueTransformations: next});
                                        }}
                                      />
                                      <div className='text-[10px] text-gray-500'>
                                        Empty = to end
                                      </div>
                                    </div>
                                  </div>
                                ) : valueTransformation.type === 'SPLIT_AND_EXTRACT' ? (
                                  <div className='flex flex-wrap items-end gap-3'>
                                    <div className='space-y-1'>
                                      <Label className='text-xs text-gray-600'>Delimiter</Label>
                                      <Input
                                        value={valueTransformation.delimiter ?? ''}
                                        placeholder=', '
                                        className='w-24'
                                        onChange={(e) => {
                                          const next = [...(local.valueTransformations || [])];
                                          next[idx] = {
                                            ...next[idx],
                                            delimiter: e.target.value,
                                          } as PayrollFileValueTransformation;
                                          updateLocal({valueTransformations: next});
                                        }}
                                      />
                                      <div className='text-[10px] text-gray-500'>
                                        e.g., ", " or " "
                                      </div>
                                    </div>
                                    <div className='space-y-1'>
                                      <Label className='text-xs text-gray-600'>
                                        Part to Extract
                                      </Label>
                                      <Input
                                        type='number'
                                        value={valueTransformation.partIndex ?? ''}
                                        placeholder='1'
                                        className='w-24'
                                        onChange={(e) => {
                                          const next = [...(local.valueTransformations || [])];
                                          const parsed = parseInt(e.target.value, 10);
                                          next[idx] = {
                                            ...next[idx],
                                            partIndex: isNaN(parsed) ? 1 : parsed,
                                          } as PayrollFileValueTransformation;
                                          updateLocal({valueTransformations: next});
                                        }}
                                      />
                                      <div className='text-[10px] text-gray-500'>
                                        1 = first, -1 = last
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className='border-emerald/20 bg-emerald/5'>
          <CardHeader className='py-4 bg-gradient-to-r from-emerald/10 to-emerald/20 border-b border-emerald/20'>
            <CardTitle className='text-sm font-semibold text-internal-brand-navy flex items-center gap-2'>
              <Eye className='h-4 w-4 text-emerald' />
              Live Preview
            </CardTitle>
          </CardHeader>
          <CardContent className='p-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div className='space-y-2'>
                <Label className='text-sm font-medium text-internal-brand-navy'>
                  Before Transformation
                </Label>
                <div className='p-3 bg-white border border-gray-200 rounded-lg font-mono text-sm'>
                  {local.exampleValuePreTransformation || (
                    <span className='text-gray-400'>No preview data available</span>
                  )}
                </div>
              </div>
              <div className='space-y-2'>
                <Label className='text-sm font-medium text-internal-brand-navy'>
                  After Transformation
                </Label>
                <div className='p-3 bg-white border border-gray-200 rounded-lg font-mono text-sm'>
                  {local.exampleValuePostTransformation || (
                    <span className='text-gray-400'>No preview data available</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  },
);

export const TransformationEditorModal = memo(
  ({
    open,
    onClose,
    editingField,
    targetFieldLabel,
    extracted,
    mapping,
    onChange,
  }: {
    open: boolean;
    onClose: () => void;
    editingField: string | null;
    targetFieldLabel: string;
    extracted: ExtractedFile[];
    mapping: Mapping | undefined;
    onChange: (next: Mapping) => void;
  }) => {
    const [isPending, startTransition] = useTransition();
    const [localMapping, setLocalMapping] = useState<Mapping | undefined>(mapping);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
      if (open) {
        setLocalMapping(mapping);
        setHasUnsavedChanges(false);
      }
    }, [open, mapping]);

    const handleMappingChange = useCallback((next: Mapping) => {
      setLocalMapping(next);
      setHasUnsavedChanges(true);
    }, []);

    const handleSave = useCallback(() => {
      if (localMapping) {
        const result = validateTransformationColumns(localMapping);
        if (!result.isValid) {
          toast({
            title: 'Invalid Transformation Found',
            description: result.errors.join('\n'),
            variant: 'destructive',
          });
          return;
        }

        startTransition(() => {
          onChange(localMapping);
          setHasUnsavedChanges(false);
        });
      }
    }, [localMapping, onChange]);

    const handleClose = useCallback(() => {
      if (hasUnsavedChanges) {
        const confirmed = window.confirm(
          'You have unsaved changes. Are you sure you want to close?',
        );
        if (confirmed) {
          onClose();
        }
      } else {
        onClose();
      }
    }, [hasUnsavedChanges, onClose]);

    return (
      <Sheet
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleClose();
        }}
      >
        <SheetContent className='w-full sm:max-w-3xl lg:max-w-5xl overflow-hidden flex flex-col'>
          <SheetHeader className='shrink-0'>
            <SheetTitle className='flex items-center gap-2 text-internal-brand-navy'>
              <Wand2 className='h-5 w-5 text-internal-brand-blue' />
              Advanced Field Mapping
            </SheetTitle>
            <SheetDescription>
              Configure advanced transformations for{' '}
              <span className='font-semibold text-internal-brand-navy'>{targetFieldLabel}</span>
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className='flex-1'>
            <div className='py-4 pr-4'>
              {!!editingField && (
                <TransformationEditor
                  extracted={extracted}
                  mapping={localMapping}
                  onChange={handleMappingChange}
                />
              )}
            </div>
          </ScrollArea>
          <SheetFooter className='shrink-0 pt-2 gap-2'>
            <Button variant='outline' onClick={handleClose} disabled={isPending}>
              Close
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges || isPending}
              className='bg-internal-brand-blue hover:bg-internal-brand-blue/90 text-white'
            >
              <Check className='h-4 w-4 mr-2' />
              {isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  },
);
